import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'nuqs';
import {
  ChevronRight,
  Eye,
  Pencil,
  Search,
  TerminalSquare,
  TriangleAlert,
  Wrench,
  X,
} from 'lucide-react';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import {
  HowToUseSection,
  HowToUseSteps,
  HowToUseStep,
  HowToUseParagraph,
  HowToUseCode,
  HowToUseBullets,
  HowToUseBullet,
  HowToUseLabel,
} from '@reflex/ui/components/integration/HowToUse';
import { CodeBlock } from '@/components/content/CodeBlock';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { Input } from '@reflex/ui/components/ui/input';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@reflex/ui/components/ui/sheet';
import { cn } from '@reflex/ui/lib/utils';
import { api } from '@/lib/api';

interface McpToolSummary {
  name: string;
  title?: string;
  description?: string;
  readOnly?: boolean;
  /** The tool irreversibly destroys data (`annotations.destructiveHint`). */
  destructive?: boolean;
  inputSchema?: Record<string, unknown>;
}

interface McpToolParameter {
  name: string;
  type: string;
  description?: string;
  required: boolean;
}

function ToolIcon({ readOnly, className }: { readOnly?: boolean; className?: string }) {
  if (readOnly === true) return <Eye className={className} aria-hidden="true" />;
  if (readOnly === false) return <Pencil className={className} aria-hidden="true" />;
  return <Wrench className={className} aria-hidden="true" />;
}

const ACCESS_TAG_CLASS =
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs font-medium';

/**
 * Access tags: what running the tool can do to workspace state, at a glance.
 * "Read-only" / "Write" come from the tool's `readOnlyHint`; "Destructive"
 * (an irreversible delete, from `destructiveHint`) rides beside "Write" when
 * the server declares it. A tool with no hints gets no tags rather than a
 * guess.
 */
function ToolAccessTags({ tool, className }: { tool: McpToolSummary; className?: string }) {
  const readOnlyLabel =
    tool.readOnly === true ? 'Read-only' : tool.readOnly === false ? 'Write' : null;
  if (readOnlyLabel == null && tool.destructive !== true) return null;
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)}>
      {readOnlyLabel != null ? (
        <span
          className={cn(
            ACCESS_TAG_CLASS,
            tool.readOnly
              ? 'border-status-success/25 bg-status-success/15 text-status-success'
              : 'border-status-warning/25 bg-status-warning/15 text-status-warning',
          )}
        >
          <ToolIcon readOnly={tool.readOnly} className="h-3 w-3" />
          {readOnlyLabel}
        </span>
      ) : null}
      {tool.destructive === true ? (
        <span
          className={cn(
            ACCESS_TAG_CLASS,
            'border-destructive/25 bg-destructive/15 text-destructive',
          )}
        >
          <TriangleAlert className="h-3 w-3" aria-hidden="true" />
          Destructive
        </span>
      ) : null}
    </span>
  );
}

function extractParameters(schema: Record<string, unknown> | undefined): McpToolParameter[] {
  if (!schema || typeof schema !== 'object') return [];
  const props = schema.properties;
  if (!props || typeof props !== 'object') return [];
  const requiredList = Array.isArray(schema.required) ? (schema.required as unknown[]) : [];
  const required = new Set(requiredList.filter((v): v is string => typeof v === 'string'));
  return Object.entries(props as Record<string, unknown>).map(([name, raw]) => {
    const prop = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
    let type: string;
    if (typeof prop.type === 'string') {
      type = prop.type;
    } else if (Array.isArray(prop.type)) {
      type = prop.type.filter((t): t is string => typeof t === 'string').join(' | ') || 'unknown';
    } else if (Array.isArray(prop.enum)) {
      type = 'enum';
    } else {
      type = 'unknown';
    }
    const description = typeof prop.description === 'string' ? prop.description : undefined;
    return { name, type, description, required: required.has(name) };
  });
}

function getMcpUrl(): string {
  return `${window.location.origin}/api/mcp`;
}

function buildConfigJson(url: string): string {
  return JSON.stringify({ mcpServers: { reflex: { url } } }, null, 2);
}

function buildClaudeCodeConfigJson(url: string): string {
  return JSON.stringify({ mcpServers: { reflex: { type: 'url', url } } }, null, 2);
}

export function McpSetupPage({ embedded = false }: { embedded?: boolean }) {
  const mcpUrl = useMemo(() => getMcpUrl(), []);
  const configJson = useMemo(() => buildConfigJson(mcpUrl), [mcpUrl]);
  const claudeCodeConfigJson = useMemo(() => buildClaudeCodeConfigJson(mcpUrl), [mcpUrl]);
  const toolsQuery = useQuery({
    queryKey: ['mcp', 'tools'],
    queryFn: () => api.getMcpTools(),
    staleTime: 60_000,
  });
  const tools = useMemo<McpToolSummary[]>(
    () => toolsQuery.data?.tools ?? [],
    [toolsQuery.data?.tools],
  );
  const [search, setSearch] = useQueryState('q');
  const [activeToolName, setActiveToolName] = useQueryState('tool');
  const query = (search ?? '').trim().toLowerCase();
  const filteredTools = useMemo(() => {
    if (!query) return tools;
    return tools.filter((t) => {
      const haystack = `${t.name} ${t.title ?? ''} ${t.description ?? ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [tools, query]);
  const activeTool = useMemo(
    () => (activeToolName ? (tools.find((t) => t.name === activeToolName) ?? null) : null),
    [tools, activeToolName],
  );
  const activeParameters = useMemo(
    () => (activeTool ? extractParameters(activeTool.inputSchema) : []),
    [activeTool],
  );

  const body = (
    <>
      <PageHeader
        {...(embedded
          ? {}
          : {
              iconVariant: 'box' as const,
              icon: <TerminalSquare className="h-5 w-5 text-muted-foreground" />,
              title: 'MCP Setup',
            })}
        description={
          embedded
            ? 'Connect Cursor, Claude Desktop, or any MCP-compatible client from your editor.'
            : 'Connect Cursor, Claude Desktop, or any MCP-compatible client to start and monitor agents from your editor. Also available as a built-in MCP server named `reflex` you can attach to any agent or persona from the MCP Servers page.'
        }
        docs="https://docs.runloop.ai/docs/devboxes/mcp-hub"
        howToUse={{
          label: 'How to connect via MCP',
          children: (
            <>
              <HowToUseSection title="Cursor">
                <HowToUseSteps>
                  <HowToUseStep>
                    Open your project&apos;s <HowToUseCode>.cursor/mcp.json</HowToUseCode> (or{' '}
                    <HowToUseCode>~/.cursor/mcp.json</HowToUseCode> for global).
                  </HowToUseStep>
                  <HowToUseStep>Add the following configuration:</HowToUseStep>
                </HowToUseSteps>
                <div className="mt-2">
                  <CodeBlock title=".cursor/mcp.json">{configJson}</CodeBlock>
                </div>
                <HowToUseSteps>
                  <HowToUseStep>
                    Restart Cursor. If OAuth is enabled, a browser window will open to authenticate.
                  </HowToUseStep>
                </HowToUseSteps>
              </HowToUseSection>

              <HowToUseSection title="Claude Code">
                <HowToUseSteps>
                  <HowToUseStep>
                    Run the following command from your project directory:
                  </HowToUseStep>
                </HowToUseSteps>
                <div className="mt-2">
                  <CodeBlock title="Terminal">{`claude mcp add --transport http reflex ${mcpUrl}`}</CodeBlock>
                </div>
                <HowToUseParagraph>
                  Or edit <HowToUseCode>.mcp.json</HowToUseCode> in your project root directly:
                </HowToUseParagraph>
                <div className="mt-2">
                  <CodeBlock title=".mcp.json">{claudeCodeConfigJson}</CodeBlock>
                </div>
                <HowToUseParagraph>
                  For global access across all projects, add{' '}
                  <HowToUseCode>--scope user</HowToUseCode> to the CLI command.
                </HowToUseParagraph>
              </HowToUseSection>

              <HowToUseSection title="Claude Desktop">
                <HowToUseSteps>
                  <HowToUseStep>
                    Open <HowToUseLabel>Settings</HowToUseLabel> &rarr;{' '}
                    <HowToUseLabel>Connectors</HowToUseLabel>.
                  </HowToUseStep>
                  <HowToUseStep>
                    Click <HowToUseLabel>Add</HowToUseLabel> and enter the URL:
                  </HowToUseStep>
                </HowToUseSteps>
                <div className="mt-2">
                  <CodeBlock title="MCP Server URL">{mcpUrl}</CodeBlock>
                </div>
                <HowToUseParagraph>
                  If OAuth is enabled, Claude Desktop will open your browser to authenticate on
                  first use. Remote connectors require a Pro, Max, Team, or Enterprise plan.
                </HowToUseParagraph>
              </HowToUseSection>

              <HowToUseSection title="Authentication">
                <HowToUseParagraph>
                  Three authentication modes are supported depending on your server configuration:
                </HowToUseParagraph>
                <HowToUseBullets>
                  <HowToUseBullet>
                    <HowToUseLabel>No auth</HowToUseLabel>: If OAuth is not configured on the
                    server, the MCP endpoint works without authentication.
                  </HowToUseBullet>
                  <HowToUseBullet>
                    <HowToUseLabel>OAuth 2.1</HowToUseLabel>: When GitHub or Google OAuth is
                    enabled, your client handles the browser-based flow automatically on first
                    connection.
                  </HowToUseBullet>
                  <HowToUseBullet>
                    <HowToUseLabel>Bearer token</HowToUseLabel>: Pass an existing session token via
                    an <HowToUseCode>Authorization</HowToUseCode> header for programmatic access.
                  </HowToUseBullet>
                </HowToUseBullets>
              </HowToUseSection>
            </>
          ),
        }}
      />

      <section>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <SectionLabel className="mb-0">
            Available Tools
            {tools.length > 0
              ? query
                ? ` (${filteredTools.length} of ${tools.length})`
                : ` (${tools.length})`
              : ''}
          </SectionLabel>
          <div className="relative w-full max-w-xs">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              placeholder="Search tools..."
              aria-label="Search tools"
              className="h-8 pl-8 pr-8 text-xs"
              value={search ?? ''}
              onChange={(e) => void setSearch(e.target.value || null)}
            />
            {search ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Clear search"
                className="absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2"
                onClick={() => void setSearch(null)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>
        </div>
        {toolsQuery.isLoading ? (
          <PageDescription className="text-xs">Loading tools…</PageDescription>
        ) : toolsQuery.isError ? (
          <PageDescription className="text-xs text-destructive">
            Failed to load tools from the MCP server.
          </PageDescription>
        ) : tools.length === 0 ? (
          <PageDescription className="text-xs">No tools registered.</PageDescription>
        ) : filteredTools.length === 0 ? (
          <PageDescription className="text-xs">
            No tools match &ldquo;{search}&rdquo;.
          </PageDescription>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {filteredTools.map((tool) => {
              const { name, title, description, readOnly } = tool;
              return (
                <button
                  key={name}
                  type="button"
                  data-testid={`mcp-tool-${name}`}
                  aria-label={`View details for ${name}`}
                  onClick={() => void setActiveToolName(name)}
                  className="group flex h-full w-full items-start gap-3 rounded-lg border border-border bg-card p-3 text-left transition hover:border-border/80 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                    <ToolIcon readOnly={readOnly} className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-mono text-xs font-medium" title={name}>
                      {name}
                    </p>
                    {title && title !== name ? (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground" title={title}>
                        {title}
                      </p>
                    ) : null}
                    {description ? (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {description}
                      </p>
                    ) : null}
                    <ToolAccessTags tool={tool} className="mt-1.5" />
                  </div>
                  <ChevronRight
                    aria-hidden="true"
                    className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground/40 transition group-hover:text-muted-foreground"
                  />
                </button>
              );
            })}
          </div>
        )}
      </section>

      <Sheet
        open={!!activeTool}
        onOpenChange={(open) => {
          if (!open) void setActiveToolName(null);
        }}
      >
        <SheetContent
          side="right"
          className="overflow-y-auto p-0 sm:max-w-md"
          aria-describedby={undefined}
        >
          {activeTool ? (
            <ToolDetail tool={activeTool} parameters={activeParameters} />
          ) : (
            <SheetHeader>
              <SheetTitle>Tool not found</SheetTitle>
              <SheetDescription>
                No tool named &ldquo;{activeToolName}&rdquo; is registered.
              </SheetDescription>
            </SheetHeader>
          )}
        </SheetContent>
      </Sheet>
    </>
  );

  if (embedded) return body;

  return <PageLayout maxWidth="3xl">{body}</PageLayout>;
}

function ToolDetail({
  tool,
  parameters,
}: {
  tool: McpToolSummary;
  parameters: McpToolParameter[];
}) {
  return (
    <div data-testid="mcp-tool-detail" className="flex flex-col">
      <SheetHeader className="border-b border-border/50">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
            <ToolIcon readOnly={tool.readOnly} className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1 pr-6">
            <SheetTitle className="break-all font-mono text-sm">{tool.name}</SheetTitle>
            {tool.title && tool.title !== tool.name ? (
              <SheetDescription className="mt-0.5 text-sm">{tool.title}</SheetDescription>
            ) : null}
          </div>
        </div>
        <ToolAccessTags tool={tool} className="mt-1" />
      </SheetHeader>

      <div className="flex flex-col gap-4 px-4 pb-6 pt-4">
        {tool.description ? (
          <section>
            <SectionLabel className="mb-1.5">Description</SectionLabel>
            <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
              {tool.description}
            </p>
          </section>
        ) : null}

        <section>
          <SectionLabel className="mb-1.5">
            Parameters{parameters.length > 0 ? ` (${parameters.length})` : ''}
          </SectionLabel>
          {parameters.length === 0 ? (
            <p className="text-xs text-muted-foreground">No parameters.</p>
          ) : (
            <ul className="space-y-1.5">
              {parameters.map((param) => (
                <li key={param.name} className="rounded-md border border-border/50 bg-muted/20 p-2">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <code className="font-mono text-xs font-medium text-foreground">
                      {param.name}
                    </code>
                    <span className="font-mono text-2xs text-muted-foreground">{param.type}</span>
                    {param.required ? (
                      <span className="text-2xs font-medium text-status-warning">required</span>
                    ) : (
                      <span className="text-2xs text-muted-foreground/70">optional</span>
                    )}
                  </div>
                  {param.description ? (
                    <p className="mt-1 text-2xs leading-relaxed text-muted-foreground">
                      {param.description}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
